import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ChevronRightIcon,
  XMarkIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import MediaUploader from '../components/MediaManager/MediaUploader';
import FolderTree from '../components/MediaManager/FolderTree';
import MediaGallery from '../components/MediaManager/MediaGallery';
import MediaDetails from '../components/MediaManager/MediaDetails';
import FileBrowserEnhanced from '../components/MediaManager/FileBrowserEnhanced';
import { mediaApi } from '../services/mediaApi';
import PermissionGate, { usePermission } from '../components/PermissionGate';

interface MediaItem {
  id: number;
  uuid: string;
  filename: string;
  title: string;
  caption?: string;
  alt_text?: string;
  category: string;
  folder_id?: number;
  tags?: string[];
  original_path: string;
  optimized_path?: string;
  thumbnail_path?: string;
  width: number;
  height: number;
  original_size: number;
  uploaded_at: string;
  usage_count: number;
}

interface Folder {
  id: number;
  name: string;
  slug: string;
  parent_id?: number;
  path: string;
  color?: string;
  icon?: string;
  item_count: number;
}

export default function MediaManager() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canDelete, canEdit, canBulkOperation } = usePermission();
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showUploader, setShowUploader] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [page, setPage] = useState(1);

  // Fetch media items
  const { data: mediaData, isLoading: mediaLoading } = useQuery({
    queryKey: ['media', page, selectedFolder, categoryFilter, searchQuery],
    queryFn: () => mediaApi.list({
      page,
      folder_id: selectedFolder,
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      search: searchQuery || undefined
    })
  });

  // Fetch folders
  const { data: folders, isLoading: foldersLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: () => mediaApi.getFolders()
  });

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (data: { name: string; parent_id?: number }) => mediaApi.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      toast.success('Folder created successfully');
      setShowNewFolderDialog(false);
      setNewFolderName('');
    },
    onError: () => {
      toast.error('Failed to create folder');
    }
  });

  // Delete media mutation
  const deleteMediaMutation = useMutation({
    mutationFn: (id: number) => mediaApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.success('Media deleted successfully');
      setSelectedMedia(null);
    },
    onError: () => {
      toast.error('Failed to delete media');
    }
  });

  // Bulk move mutation
  const bulkMoveMutation = useMutation({
    mutationFn: (data: { media_ids: number[]; folder_id: number | null }) =>
      mediaApi.bulkMove(data.media_ids, data.folder_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      toast.success('Items moved successfully');
      setSelectedItems([]);
    },
    onError: (error: any) => {
      console.error('Move failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to move items');
    }
  });

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCreateNewFolder = () => {
    if (newFolderName.trim()) {
      createFolderMutation.mutate({
        name: newFolderName,
        parent_id: selectedFolder || undefined
      });
    }
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length > 0 && confirm(`Delete ${selectedItems.length} items?`)) {
      Promise.all(selectedItems.map(id => deleteMediaMutation.mutateAsync(id)))
        .then(() => setSelectedItems([]));
    }
  };

  const handleMoveSelected = (folderId: number) => {
    if (selectedItems.length > 0) {
      bulkMoveMutation.mutate({
        media_ids: selectedItems,
        folder_id: folderId
      });
    }
  };

  const handleCreateFolder = async (parentId: number | null, name: string) => {
    try {
      await createFolderMutation.mutateAsync({
        name,
        parent_id: parentId || undefined
      });
      toast.success('Folder created successfully');
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Enhanced File Browser Sidebar */}
      <div className="w-96 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full overflow-hidden">
        <FileBrowserEnhanced
          folders={folders || []}
          mediaItems={mediaData?.items || []}
          selectedFolder={selectedFolder}
          onFolderSelect={setSelectedFolder}
          onFileSelect={setSelectedMedia}
          onFolderCreate={handleCreateFolder}
          onFileMove={(fileId, targetFolderId) => {
            console.log('Moving file:', fileId, 'to folder:', targetFolderId);
            bulkMoveMutation.mutate({
              media_ids: [fileId],
              folder_id: targetFolderId
            });
          }}
          onFileDelete={canDelete() ? (fileId) => {
            deleteMediaMutation.mutate(fileId);
          } : undefined}
          onFileRename={(fileId, newName) => {
            // TODO: Implement rename functionality
            toast.success('Rename functionality coming soon');
          }}
          onFileDuplicate={(fileId) => {
            // Implement duplicate functionality
            toast.success('Duplicate functionality coming soon');
          }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 max-w-xl">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search media..."
                  className="w-full px-4 py-2 pl-10 pr-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-2 ml-4">
              {/* Category Filter */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">All Categories</option>
                <option value="map">Maps</option>
                <option value="diagram">Diagrams</option>
                <option value="news">News</option>
                <option value="quiz">Quiz</option>
                <option value="general">General</option>
              </select>

              {/* View Mode */}
              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                  title="Grid view"
                >
                  <Squares2X2Icon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                  title="List view"
                >
                  <ListBulletIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Upload Button */}
              <button
                onClick={() => setShowUploader(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                Upload
              </button>

              {/* Category Manager Button */}
              <button
                onClick={() => navigate('/media/categories')}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                title="Manage Categories"
              >
                <Cog6ToothIcon className="w-5 h-5 mr-2" />
                Categories
              </button>

              {/* Bulk Actions - Admin Only */}
              <PermissionGate requireAdmin>
                {selectedItems.length > 0 && (
                  <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-300 dark:border-gray-600">
                    <span className="text-sm text-gray-500">
                      {selectedItems.length} selected
                    </span>
                    <button
                      onClick={handleDeleteSelected}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      title="Delete selected"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </PermissionGate>
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center text-sm">
            <button
              onClick={() => setSelectedFolder(null)}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              All Media
            </button>
            {selectedFolder && folders && (
              <>
                <ChevronRightIcon className="w-4 h-4 mx-2 text-gray-400" />
                <span className="text-gray-900 dark:text-gray-100">
                  {folders.find(f => f.id === selectedFolder)?.name}
                </span>
              </>
            )}
            <div className="ml-auto text-gray-500">
              {mediaData?.total || 0} items
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="flex-1 overflow-y-auto p-6">
          {mediaLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading media...</p>
              </div>
            </div>
          ) : mediaData?.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <PhotoIcon className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No media files yet</p>
              <button
                onClick={() => setShowUploader(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Upload your first file
              </button>
            </div>
          ) : (
            <MediaGallery
              items={mediaData?.items || []}
              viewMode={viewMode}
              selectedItems={selectedItems}
              onSelectItem={(id) => {
                setSelectedItems(prev =>
                  prev.includes(id)
                    ? prev.filter(i => i !== id)
                    : [...prev, id]
                );
              }}
              onViewDetails={setSelectedMedia}
            />
          )}
        </div>

        {/* Pagination */}
        {mediaData && mediaData.pages > 1 && (
          <div className="px-6 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {mediaData.pages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(mediaData.pages, p + 1))}
                disabled={page === mediaData.pages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Panel */}
      {selectedMedia && (
        <MediaDetails
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['media'] });
            toast.success('Media updated');
          }}
          onDelete={canDelete() ? () => {
            deleteMediaMutation.mutate(selectedMedia.id);
          } : undefined}
        />
      )}

      {/* Upload Modal */}
      {showUploader && (
        <MediaUploader
          folderId={selectedFolder}
          category={categoryFilter === 'all' ? 'general' : categoryFilter}
          onClose={() => setShowUploader(false)}
          onUploadComplete={() => {
            queryClient.invalidateQueries({ queryKey: ['media'] });
            setShowUploader(false);
            toast.success('Upload completed');
          }}
        />
      )}

      {/* New Folder Dialog */}
      {showNewFolderDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateNewFolder()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowNewFolderDialog(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewFolder}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}